/* Gli accessi: chi può entrare nel programma, e con quale ruolo.
 *
 * Solo l'amministratore arriva qui, stessa linea già scritta in
 * `capacita.py`: dare o togliere un accesso è un mestiere diverso
 * dall'usare il programma.
 */

import { useState, type FormEvent } from "react";
import { ErroreApi, utenti, type UtenteGestito } from "../api";
import { useCarica } from "../carica";
import { useSessione } from "../sessione";

const RUOLI: { valore: string; nome: string }[] = [
  { valore: "OPERATORE", nome: "Operatore" },
  { valore: "FATTURAZIONE", nome: "Fatturazione" },
  { valore: "AMMINISTRATORE", nome: "Amministratore" },
];

/** La password provvisoria appena assegnata a una collega. Vive in memoria, qui
 * nella pagina e non nell'elenco: se la rilettura dell'elenco fallisse e
 * sparisse, con lei sparirebbe l'unica volta in cui la password si vede. */
interface Consegna {
  nome: string;
  password: string;
}

export function Utenti() {
  const elenco = useCarica<UtenteGestito[]>(() => utenti.lista(), []);
  const [consegna, setConsegna] = useState<Consegna | null>(null);

  return (
    <>

      <p>
        Chi ha un accesso al programma, e con quale ruolo. Cosa può fare ogni
        ruolo è descritto in <code>core/capacita.py</code>, non qui.
      </p>

      {consegna && <PasswordProvvisoria consegna={consegna} chiudi={() => setConsegna(null)} />}

      {elenco.errore && (
        <p className="errore" role="alert">
          {elenco.errore}
        </p>
      )}

      {elenco.dati === undefined ? (
        <p className="attesa">Un momento.</p>
      ) : (
        <>
          <NuovoUtente dopo={elenco.ricarica} />
          <Elenco elenco={elenco.dati} dopo={elenco.ricarica} consegna={setConsegna} />
        </>
      )}
    </>
  );
}

/** La password provvisoria, mostrata una sola volta: il server non la conserva
 * in chiaro, e chiudere il riquadro la fa sparire. Va comunicata di persona. */
function PasswordProvvisoria({ consegna, chiudi }: { consegna: Consegna; chiudi: () => void }) {
  const [copiata, setCopiata] = useState(false);

  async function copia() {
    try {
      await navigator.clipboard.writeText(consegna.password);
      setCopiata(true);
    } catch {
      // Su un indirizzo senza HTTPS il browser nega la copia: la password e' a
      // schermo e si seleziona con un clic.
      setCopiata(false);
    }
  }

  return (
    <section className="modulo riquadro" role="status" aria-live="polite">
      <h2>Password provvisoria per {consegna.nome}</h2>
      <p className="chiave-manuale">{consegna.password}</p>
      <p className="aiuto">
        La vedi solo adesso: non si può rivedere. Comunicala di persona. Le sue
        sessioni aperte sono state chiuse e al primo accesso dovrà cambiarla.
      </p>
      <div className="azioni-riga azioni-a-sinistra">
        <button type="button" className="tasto" onClick={() => void copia()}>
          {copiata ? "Copiata" : "Copia"}
        </button>
        <button type="button" className="tasto tasto-nudo" onClick={chiudi}>
          Fatto, l'ho comunicata
        </button>
      </div>
    </section>
  );
}

function Elenco({
  elenco,
  dopo,
  consegna,
}: {
  elenco: UtenteGestito[];
  dopo: () => Promise<void>;
  consegna: (c: Consegna) => void;
}) {
  const { utente: io } = useSessione();
  const [errore, setErrore] = useState<string | null>(null);
  const [daReimpostare, setDaReimpostare] = useState<string | null>(null);
  const [passwordDaReimpostare, setPasswordDaReimpostare] = useState<string | null>(null);

  async function reimpostaPassword(u: UtenteGestito) {
    setErrore(null);
    try {
      const risposta = await utenti.reimpostaPassword(u.identificativo);
      setPasswordDaReimpostare(null);
      consegna({ nome: u.nome, password: risposta.password_provvisoria });
      await dopo();
    } catch (e) {
      setPasswordDaReimpostare(null);
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a reimpostare la password.");
    }
  }

  async function cambia(azione: () => Promise<unknown>) {
    setErrore(null);
    try {
      await azione();
      setDaReimpostare(null);
      await dopo();
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a cambiare stato.");
    }
  }

  return (
    <section className="elenco">
      <h2>{elenco.length} accessi</h2>

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}

      <div className="tabella-scorre">
      <table className="tabella tabella-utenti">
        <tbody>
          {elenco.map((u) => (
            <tr key={u.identificativo}>
              <td>
                <span className="pratica-nome">{u.nome}</span>
                <span className="narrativa">{u.email}</span>
              </td>
              <td className="colonna-stato">
                <select
                  value={u.ruolo}
                  disabled={!u.attivo}
                  onChange={(e) =>
                    cambia(() => utenti.cambiaRuolo(u.identificativo, e.target.value))
                  }
                >
                  {RUOLI.map((r) => (
                    <option key={r.valore} value={r.valore}>
                      {r.nome}
                    </option>
                  ))}
                </select>
              </td>
              <td className="colonna-stato">
                <span className={`stato stato-${u.attivo ? "aperta" : "chiusa"}`}>
                  {u.attivo ? "attivo" : "disattivato"}
                </span>
              </td>
              <td className="colonna-stato">
                <span className={`stato stato-${u.secondo_fattore_attivo ? "aperta" : "chiusa"}`}>
                  {u.secondo_fattore_attivo ? "verifica attiva" : "verifica da attivare"}
                </span>
              </td>
              <td className="colonna-azione">
                <div className="azioni-riga">
                {/* Per chi ha dimenticato la password: il programma non manda email,
                    quindi la persona lo chiede a un amministratore. Non la propria
                    (si cambia dal nome nel menu) e non su un accesso disattivato. */}
                {u.attivo && u.identificativo !== io?.identificativo ? (
                  passwordDaReimpostare === u.identificativo ? (
                    <>
                      <span className="aiuto">Chiude le sue sessioni e crea una password provvisoria.</span>{" "}
                      <button
                        type="button"
                        className="tasto tasto-nudo"
                        onClick={() => void reimpostaPassword(u)}
                      >
                        Sì, reimposta
                      </button>
                      <button type="button" className="tasto tasto-nudo" onClick={() => setPasswordDaReimpostare(null)}>
                        No
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="tasto tasto-nudo"
                      onClick={() => setPasswordDaReimpostare(u.identificativo)}
                    >
                      Reimposta password
                    </button>
                  )
                ) : null}
                {/* Per chi ha perso il telefono e i codici di recupero. Non la propria:
                    l'ultimo amministratore passa dallo script sul server. */}
                {u.secondo_fattore_attivo && u.attivo && u.identificativo !== io?.identificativo ? (
                  daReimpostare === u.identificativo ? (
                    <>
                      <span className="aiuto">Toglie la verifica e chiude le sue sessioni.</span>{" "}
                      <button
                        type="button"
                        className="tasto tasto-nudo"
                        onClick={() => cambia(() => utenti.reimpostaSecondoFattore(u.identificativo))}
                      >
                        Sì, reimposta
                      </button>
                      <button type="button" className="tasto tasto-nudo" onClick={() => setDaReimpostare(null)}>
                        No
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="tasto tasto-nudo"
                      onClick={() => setDaReimpostare(u.identificativo)}
                    >
                      Reimposta verifica
                    </button>
                  )
                ) : null}
                {u.attivo ? (
                  <button
                    type="button"
                    className="tasto tasto-nudo"
                    onClick={() => cambia(() => utenti.disattiva(u.identificativo))}
                  >
                    Disattiva
                  </button>
                ) : (
                  <button
                    type="button"
                    className="tasto tasto-nudo"
                    onClick={() => cambia(() => utenti.riattiva(u.identificativo))}
                  >
                    Riattiva
                  </button>
                )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </section>
  );
}

function NuovoUtente({ dopo }: { dopo: () => Promise<void> }) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ruolo, setRuolo] = useState("OPERATORE");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function invia(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    setInCorso(true);
    try {
      await utenti.crea({ nome, email, password, ruolo });
      setNome("");
      setEmail("");
      setPassword("");
      setRuolo("OPERATORE");
      await dopo();
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a creare l'accesso.");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <form className="modulo riquadro" onSubmit={invia} noValidate>
      <h2>Accesso nuovo</h2>

      <div className="imputa-riga">
        <div className="campo campo-largo">
          <label htmlFor="nome">Nome</label>
          <input
            id="nome"
            value={nome}
            required
            onChange={(e) => setNome(e.target.value)}
          />
        </div>
        <div className="campo campo-largo">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            required
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </div>

      <div className="imputa-riga">
        <div className="campo campo-largo">
          <label htmlFor="password">Password provvisoria</label>
          <input
            id="password"
            value={password}
            required
            placeholder="una frase di almeno 12 caratteri"
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="campo campo-minuti">
          <label htmlFor="ruolo">Ruolo</label>
          <select id="ruolo" value={ruolo} onChange={(e) => setRuolo(e.target.value)}>
            {RUOLI.map((r) => (
              <option key={r.valore} value={r.valore}>
                {r.nome}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p className="aiuto">
        Comunicala di persona a chi la userà: dovrà cambiarla al primo accesso.
      </p>

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}

      <div>
        <button className="tasto tasto-principale" type="submit" disabled={inCorso}>
          {inCorso ? "Sto creando" : "Crea accesso"}
        </button>
      </div>
    </form>
  );
}
